import React, { useState, useEffect } from 'react';

// Default form state
const defaultFormData = {
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    mobile: '',
    company: '',
    jobTitle: '',
    status: 'new',
    source: 'website',
    score: 0,
    address: '',
    city: '',
    state: '',
    country: '',
    notes: '',
    tags: [],
};

// Status options
const statusOptions = [
    { value: 'new', label: 'New' },
    { value: 'contacted', label: 'Contacted' },
    { value: 'qualified', label: 'Qualified' },
    { value: 'unqualified', label: 'Unqualified' },
];

// Source options
const sourceOptions = [
    { value: 'website', label: 'Website' },
    { value: 'referral', label: 'Referral' },
    { value: 'cold_call', label: 'Cold Call' },
    { value: 'advertisement', label: 'Advertisement' },
    { value: 'social_media', label: 'Social Media' },
    { value: 'walk_in', label: 'Walk-in' },
    { value: 'other', label: 'Other' },
];

function LeadForm({ lead, onSubmit, onCancel, loading }) {
    const [formData, setFormData] = useState(defaultFormData);
    const [errors, setErrors] = useState({});

    // Populate form when lead prop changes
    useEffect(() => {
        if (lead) {
            const fullName = lead.name || '';
            const nameParts = fullName.trim().split(/\s+/);
            const fallbackFirstName = nameParts.length > 0 ? nameParts[0] : '';
            const fallbackLastName = nameParts.length > 1 ? nameParts.slice(1).join(' ') : '';
            setFormData({
                firstName: lead.firstName || fallbackFirstName || '',
                lastName: lead.lastName || fallbackLastName || '',
                email: lead.email || '',
                phone: lead.phone || '',
                mobile: lead.mobile || '',
                company: lead.company || '',
                jobTitle: lead.jobTitle || '',
                status: lead.status || 'new',
                source: lead.source || 'website',
                score: lead.score || 0,
                address: lead.address || '',
                city: lead.city || '',
                state: lead.state || '',
                country: lead.country || '',
                notes: lead.notes || '',
                tags: lead.tags || [],
            });
        } else {
            setFormData(defaultFormData);
        }
        setErrors({});
    }, [lead]);

    const handleChange = (e) => {
        const { name, value, type } = e.target;
        const newValue = type === 'number' ? parseInt(value, 10) || 0 : value;

        setFormData((prev) => ({
            ...prev,
            [name]: newValue,
        }));

        // Clear field error when user types
        if (errors[name]) {
            setErrors((prev) => {
                const newErrors = { ...prev };
                delete newErrors[name];
                return newErrors;
            });
        }
    };

    const validate = () => {
        const newErrors = {};

        if (!formData.firstName.trim()) {
            newErrors.firstName = 'First name is required';
        }

        if (!formData.lastName.trim()) {
            newErrors.lastName = 'Last name is required';
        }

        if (!formData.email.trim()) {
            newErrors.email = 'Email is required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            newErrors.email = 'Please enter a valid email address';
        }

        if (!formData.source) {
            newErrors.source = 'Source is required';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        if (validate()) {
            onSubmit?.(formData);
        }
    };

    return (
        <form onSubmit={handleSubmit}>
            {/* Basic Info Section */}
            <div style={{ marginBottom: '24px' }}>
                <h4 style={{ margin: '0 0 16px 0', fontSize: '14px', fontWeight: '600', color: 'var(--crm-gray-600)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Basic Information
                </h4>
                <div className="crm-form-row">
                    <div className="crm-form-group">
                        <label className="crm-label required">First Name</label>
                        <input
                            type="text"
                            name="firstName"
                            value={formData.firstName}
                            onChange={handleChange}
                            className={`crm-input ${errors.firstName ? 'crm-input-error' : ''}`}
                            placeholder="Enter first name"
                        />
                        {errors.firstName && <p className="crm-error-message">{errors.firstName}</p>}
                    </div>

                    <div className="crm-form-group">
                        <label className="crm-label required">Last Name</label>
                        <input
                            type="text"
                            name="lastName"
                            value={formData.lastName}
                            onChange={handleChange}
                            className={`crm-input ${errors.lastName ? 'crm-input-error' : ''}`}
                            placeholder="Enter last name"
                        />
                        {errors.lastName && <p className="crm-error-message">{errors.lastName}</p>}
                    </div>
                </div>

                <div className="crm-form-row">
                    <div className="crm-form-group">
                        <label className="crm-label required">Email</label>
                        <input
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            className={`crm-input ${errors.email ? 'crm-input-error' : ''}`}
                            placeholder="email@example.com"
                        />
                        {errors.email && <p className="crm-error-message">{errors.email}</p>}
                    </div>

                    <div className="crm-form-group">
                        <label className="crm-label">Phone</label>
                        <input
                            type="tel"
                            name="phone"
                            value={formData.phone}
                            onChange={handleChange}
                            className="crm-input"
                            placeholder="Enter phone number"
                        />
                    </div>
                </div>

                <div className="crm-form-group">
                    <label className="crm-label">Mobile</label>
                    <input
                        type="tel"
                        name="mobile"
                        value={formData.mobile}
                        onChange={handleChange}
                        className="crm-input"
                        placeholder="Enter mobile number"
                    />
                </div>
            </div>

            {/* Company Info Section */}
            <div style={{ marginBottom: '24px' }}>
                <h4 style={{ margin: '0 0 16px 0', fontSize: '14px', fontWeight: '600', color: 'var(--crm-gray-600)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Company Information
                </h4>
                <div className="crm-form-row">
                    <div className="crm-form-group">
                        <label className="crm-label">Company</label>
                        <input
                            type="text"
                            name="company"
                            value={formData.company}
                            onChange={handleChange}
                            className="crm-input"
                            placeholder="Enter company name"
                        />
                    </div>

                    <div className="crm-form-group">
                        <label className="crm-label">Job Title</label>
                        <input
                            type="text"
                            name="jobTitle"
                            value={formData.jobTitle}
                            onChange={handleChange}
                            className="crm-input"
                            placeholder="Enter job title"
                        />
                    </div>
                </div>
            </div>

            {/* Lead Details Section */}
            <div style={{ marginBottom: '24px' }}>
                <h4 style={{ margin: '0 0 16px 0', fontSize: '14px', fontWeight: '600', color: 'var(--crm-gray-600)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Lead Details
                </h4>
                <div className="crm-form-row">
                    <div className="crm-form-group">
                        <label className="crm-label">Status</label>
                        <select
                            name="status"
                            value={formData.status}
                            onChange={handleChange}
                            className="crm-select"
                        >
                            {statusOptions.map((opt) => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                        </select>
                    </div>

                    <div className="crm-form-group">
                        <label className="crm-label required">Source</label>
                        <select
                            name="source"
                            value={formData.source}
                            onChange={handleChange}
                            className={`crm-select ${errors.source ? 'crm-input-error' : ''}`}
                        >
                            {sourceOptions.map((opt) => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                        </select>
                        {errors.source && <p className="crm-error-message">{errors.source}</p>}
                    </div>

                    <div className="crm-form-group">
                        <label className="crm-label">Score (0-100)</label>
                        <input
                            type="number"
                            name="score"
                            value={formData.score}
                            onChange={handleChange}
                            className="crm-input"
                            min="0"
                            max="100"
                            placeholder="0"
                        />
                    </div>
                </div>
            </div>

            {/* Location Section */}
            <div style={{ marginBottom: '24px' }}>
                <h4 style={{ margin: '0 0 16px 0', fontSize: '14px', fontWeight: '600', color: 'var(--crm-gray-600)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Location
                </h4>
                <div className="crm-form-group">
                    <label className="crm-label">Address</label>
                    <input
                        type="text"
                        name="address"
                        value={formData.address}
                        onChange={handleChange}
                        className="crm-input"
                        placeholder="Enter address"
                    />
                </div>

                <div className="crm-form-row">
                    <div className="crm-form-group">
                        <label className="crm-label">City</label>
                        <input
                            type="text"
                            name="city"
                            value={formData.city}
                            onChange={handleChange}
                            className="crm-input"
                            placeholder="Enter city"
                        />
                    </div>

                    <div className="crm-form-group">
                        <label className="crm-label">State</label>
                        <input
                            type="text"
                            name="state"
                            value={formData.state}
                            onChange={handleChange}
                            className="crm-input"
                            placeholder="Enter state"
                        />
                    </div>

                    <div className="crm-form-group">
                        <label className="crm-label">Country</label>
                        <input
                            type="text"
                            name="country"
                            value={formData.country}
                            onChange={handleChange}
                            className="crm-input"
                            placeholder="Enter country"
                        />
                    </div>
                </div>
            </div>

            {/* Notes Section */}
            <div style={{ marginBottom: '24px' }}>
                <h4 style={{ margin: '0 0 16px 0', fontSize: '14px', fontWeight: '600', color: 'var(--crm-gray-600)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Notes
                </h4>
                <div className="crm-form-group">
                    <textarea
                        name="notes"
                        value={formData.notes}
                        onChange={handleChange}
                        className="crm-textarea"
                        placeholder="Add any notes about this lead..."
                        rows={4}
                    />
                </div>
            </div>

            {/* Form Actions */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', paddingTop: '16px', borderTop: '1px solid var(--crm-gray-200)' }}>
                <button
                    type="button"
                    className="crm-btn crm-btn-secondary"
                    onClick={onCancel}
                    disabled={loading}
                >
                    Cancel
                </button>
                <button
                    type="submit"
                    className="crm-btn crm-btn-primary"
                    disabled={loading}
                >
                    {loading ? 'Saving...' : (lead ? 'Update Lead' : 'Create Lead')}
                </button>
            </div>
        </form>
    );
}

export default LeadForm;
